import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Cable, Loader2, CheckCircle2, Link as LinkIcon, KeyRound, RefreshCw, ExternalLink, AlertTriangle, PlayCircle, Eye, Bell } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const inp = "w-full bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder-[#64748B] focus:outline-none focus:border-primary";
const btn = "inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

export default function ClickUpSettings() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [tokenDraft, setTokenDraft] = useState("");
  const [workspaces, setWorkspaces] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [folders, setFolders] = useState([]);
  const [folderLists, setFolderLists] = useState([]);
  const [folderlessLists, setFolderlessLists] = useState([]);
  const [loadingLists, setLoadingLists] = useState(false);
  const [chatChannels, setChatChannels] = useState([]);
  const [loadingChat, setLoadingChat] = useState(false);
  const [testingChat, setTestingChat] = useState(false);
  const [sacBusy, setSacBusy] = useState("");        // "" | "daily" | "morning" | "watcher"
  const [sacPreview, setSacPreview] = useState(""); // last previewed report text
  const [clickupUsers, setClickupUsers] = useState([]); // for untreated watcher picker

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clickup/config");
      setCfg(data);
    } catch (e) {
      toast.error(formatApiError(e, "Falha a carregar config ClickUp"));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Preload the ClickUp workspace member list once the workspace is configured
  // — used by the untreated-alert watcher picker.
  useEffect(() => {
    if (cfg?.workspace_id && cfg?.token_set) {
      loadClickupUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg?.workspace_id, cfg?.token_set]);

  const loadClickupUsers = async () => {
    try {
      const { data } = await api.get("/clickup/workspace-members");
      setClickupUsers(data.items || []);
    } catch (e) {
      // Silent — the watcher UI will fall back to manual ID entry.
    }
  };

  // Once we have a verified token, auto-populate workspace list
  useEffect(() => {
    if (cfg?.token_set && cfg?.last_verified_at) {
      loadWorkspaces();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg?.token_set, cfg?.last_verified_at]);

  // When workspace selected → load spaces
  useEffect(() => {
    if (cfg?.workspace_id) loadSpaces(cfg.workspace_id);
    else setSpaces([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg?.workspace_id]);

  // When space selected → load folders + folderless lists
  useEffect(() => {
    if (cfg?.default_space_id) {
      loadFolders(cfg.default_space_id);
      loadFolderlessLists(cfg.default_space_id);
    } else {
      setFolders([]);
      setFolderlessLists([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg?.default_space_id]);

  const savePartial = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.put("/clickup/config", patch);
      setCfg(data);
      return data;
    } catch (e) {
      toast.error(formatApiError(e, "Falha ao guardar"));
      throw e;
    } finally { setSaving(false); }
  };

  const saveToken = async () => {
    if (!tokenDraft.trim()) { toast.error("Cole o Personal API Token"); return; }
    await savePartial({ api_token: tokenDraft.trim() });
    setTokenDraft("");
    toast.success("Token guardado. Clica em Testar ligação para validar.");
  };

  const clearToken = async () => {
    if (!window.confirm("Remover o token guardado?")) return;
    await savePartial({ api_token: "", enabled: false });
    setWorkspaces([]); setSpaces([]); setFolders([]);
    setFolderLists([]); setFolderlessLists([]);
    toast.success("Token removido.");
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/clickup/test-connection");
      toast.success(`Ligação OK — ${data.workspaces?.length || 0} workspace(s) encontrado(s)`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Falha ao testar ligação"));
    } finally { setTesting(false); }
  };

  const loadWorkspaces = async () => {
    try {
      const { data } = await api.get("/clickup/workspaces");
      setWorkspaces(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e, "Erro ao listar workspaces"));
    }
  };

  const loadSpaces = async (teamId) => {
    setLoadingLists(true);
    try {
      const { data } = await api.get("/clickup/spaces", { params: { team_id: teamId } });
      setSpaces(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e, "Erro ao listar spaces"));
    } finally { setLoadingLists(false); }
  };

  const loadFolders = async (spaceId) => {
    try {
      const { data } = await api.get("/clickup/folders", { params: { space_id: spaceId } });
      setFolders(data.items || []);
    } catch (e) { /* silent */ }
  };

  const loadFolderlessLists = async (spaceId) => {
    try {
      const { data } = await api.get("/clickup/lists", { params: { space_id: spaceId } });
      setFolderlessLists(data.items || []);
    } catch (e) { /* silent */ }
  };

  const loadFolderLists = async (folderId) => {
    try {
      const { data } = await api.get("/clickup/lists", { params: { folder_id: folderId } });
      setFolderLists(prev => {
        // group per folder id so the UI can display all folders at once
        const withoutThis = prev.filter(p => p.__folder !== folderId);
        return [...withoutThis, ...((data.items || []).map(it => ({ ...it, __folder: folderId })))];
      });
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    // Prefetch lists for each folder when folders load (small N, safe)
    if (folders.length > 0) folders.forEach(f => loadFolderLists(f.id));
    else setFolderLists([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folders]);

  const allListChoices = useMemo(() => {
    const rows = [
      ...folderlessLists.map(l => ({ id: l.id, name: `${l.name} · (sem folder)` })),
      ...folderLists.map(l => {
        const f = folders.find(x => x.id === l.__folder);
        return { id: l.id, name: `${l.name} · ${f?.name || "folder"}` };
      }),
    ];
    return rows;
  }, [folderlessLists, folderLists, folders]);

  // Chat channels — load when workspace is picked and integration verified
  const loadChatChannels = async () => {
    if (!cfg?.workspace_id) return;
    setLoadingChat(true);
    try {
      const { data } = await api.get("/clickup/chat/channels");
      setChatChannels(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e, "Erro ao listar canais de chat"));
    } finally { setLoadingChat(false); }
  };

  useEffect(() => {
    if (cfg?.workspace_id && cfg?.last_verified_at) loadChatChannels();
    else setChatChannels([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg?.workspace_id, cfg?.last_verified_at]);

  const sendChatTest = async () => {
    if (!cfg.celebration_channel_id) {
      toast.error("Escolha primeiro o canal");
      return;
    }
    setTestingChat(true);
    try {
      const { data } = await api.post("/clickup/chat/send-test", {
        channel_id: cfg.celebration_channel_id,
      });
      toast.success(`Mensagem de teste enviada (id: ${data.message_id || "?"})`);
    } catch (e) {
      toast.error(formatApiError(e, "Falha ao enviar mensagem de teste"));
    } finally { setTestingChat(false); }
  };

  if (loading || !cfg) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> A carregar…
      </div>
    );
  }

  const verifiedRecently = !!cfg.last_verified_at;

  return (
    <div className="max-w-3xl mx-auto py-10 px-6 space-y-8" data-testid="clickup-settings">
      <header className="space-y-1">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
          <Cable className="w-3 h-3" /> Integração
        </div>
        <h1 className="text-3xl font-heading font-semibold">ClickUp</h1>
        <p className="text-sm text-muted-foreground">
          Ligue a sua conta ClickUp para sincronizar tarefas de Cliente, Recrutamento e DAF.
        </p>
      </header>

      {/* ---------------- STEP 1 · Credencial ---------------- */}
      <div className="bg-card border border-border rounded-2xl p-8 space-y-5">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-full bg-muted border border-border flex items-center justify-center text-[13px] font-semibold text-muted-foreground">
            1
          </div>
          <h2 className="text-lg font-heading font-semibold flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-foreground" /> Personal API Token
            {verifiedRecently && (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium bg-[hsl(var(--success-bg))] text-[hsl(var(--success))] border border-[hsl(var(--success))]/40 rounded-full px-2 py-0.5">
                <CheckCircle2 className="w-3 h-3" /> Verificado
              </span>
            )}
          </h2>
        </div>
        <p className="text-sm text-muted-foreground">
          Obtenha um token pessoal em ClickUp → <b>Settings → Apps → Generate</b>. O token começa com{" "}
          <code className="text-xs bg-muted rounded px-1">pk_</code> e não expira.{" "}
          <a href="https://app.clickup.com/settings/apps" target="_blank" rel="noreferrer"
             className="text-foreground hover:underline inline-flex items-center gap-1">
            Abrir ClickUp <ExternalLink className="w-3 h-3" />
          </a>
        </p>

        {cfg.token_set ? (
          <div className="rounded-xl border border-[hsl(var(--success))]/25 bg-[hsl(var(--success-bg))] p-4 space-y-2 text-sm text-[hsl(var(--success))]" data-testid="clickup-token-present">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              <span>Token guardado: <code className="bg-card/60 px-1.5 py-0.5 rounded text-[12px]">{cfg.token_preview || "••••••"}</code></span>
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <button onClick={testConnection} disabled={testing}
                      className={`${btn} bg-emerald-600 hover:bg-emerald-700 text-primary-foreground`}
                      data-testid="clickup-test-btn">
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <LinkIcon className="w-4 h-4" />} Testar ligação
              </button>
              <button onClick={clearToken}
                      className={`${btn} bg-card border border-border hover:bg-muted/40 text-foreground`}
                      data-testid="clickup-clear-token">
                Remover token
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <label className="block">
              <span className="block text-[11px] uppercase tracking-widest text-muted-foreground mb-1">Personal API Token</span>
              <input type="password" className={inp} placeholder="pk_XXXXXXXXXXXX…"
                     value={tokenDraft} onChange={e => setTokenDraft(e.target.value)}
                     data-testid="clickup-token-input" autoComplete="off" />
            </label>
            <button onClick={saveToken} disabled={saving || !tokenDraft.trim()}
                    className={`${btn} bg-foreground hover:bg-slate-800 text-background`}
                    data-testid="clickup-save-token">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Guardar token
            </button>
          </div>
        )}
      </div>

      {/* ---------------- STEP 2 · Escolher Workspace / Space / List ---------------- */}
      <div className={`bg-card border border-border rounded-2xl p-8 space-y-5 ${!cfg.token_set ? "opacity-60 pointer-events-none" : ""}`}
           data-testid="clickup-picker">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-full bg-muted border border-border flex items-center justify-center text-[13px] font-semibold text-muted-foreground">2</div>
          <h2 className="text-lg font-heading font-semibold">Workspace, Space e List predefinidos</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          Selecione onde as tarefas serão criadas por defeito. Estas escolhas ficam guardadas.
          A qualquer momento clique em <RefreshCw className="w-3 h-3 inline align-baseline" /> para actualizar a árvore.
        </p>

        {/* Workspace */}
        <label className="block">
          <span className="block text-[11px] uppercase tracking-widest text-muted-foreground mb-1">Workspace (Team)</span>
          <div className="flex gap-2">
            <select value={cfg.workspace_id || ""} className={inp}
                    onChange={async (e) => {
                      const w = workspaces.find(x => x.id === e.target.value);
                      await savePartial({
                        workspace_id: w?.id || "",
                        workspace_name: w?.name || "",
                        default_space_id: "", default_space_name: "",
                        default_list_id: "", default_list_name: "",
                      });
                    }}
                    data-testid="clickup-workspace-select">
              <option value="">— seleccionar —</option>
              {workspaces.map(w => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
            </select>
            <button onClick={loadWorkspaces} className={`${btn} bg-muted hover:bg-muted text-foreground`}
                    data-testid="clickup-reload-workspaces">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </label>

        {/* Space */}
        {cfg.workspace_id && (
          <label className="block">
            <span className="block text-[11px] uppercase tracking-widest text-muted-foreground mb-1">Space</span>
            <div className="flex gap-2">
              <select value={cfg.default_space_id || ""} className={inp}
                      disabled={loadingLists}
                      onChange={async (e) => {
                        const s = spaces.find(x => x.id === e.target.value);
                        await savePartial({
                          default_space_id: s?.id || "",
                          default_space_name: s?.name || "",
                          default_list_id: "", default_list_name: "",
                        });
                      }}
                      data-testid="clickup-space-select">
                <option value="">— seleccionar —</option>
                {spaces.map(s => <option key={s.id} value={s.id}>{s.name}{s.private ? " · privado" : ""}</option>)}
              </select>
              <button onClick={() => loadSpaces(cfg.workspace_id)} className={`${btn} bg-muted hover:bg-muted text-foreground`}
                      data-testid="clickup-reload-spaces">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </label>
        )}

        {/* List */}
        {cfg.default_space_id && (
          <label className="block">
            <span className="block text-[11px] uppercase tracking-widest text-muted-foreground mb-1">List predefinida</span>
            <div className="flex gap-2">
              <select value={cfg.default_list_id || ""} className={inp}
                      onChange={async (e) => {
                        const l = allListChoices.find(x => x.id === e.target.value);
                        await savePartial({
                          default_list_id: l?.id || "",
                          default_list_name: l?.name || "",
                        });
                      }}
                      data-testid="clickup-list-select">
                <option value="">— seleccionar —</option>
                {allListChoices.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
              <button onClick={() => { loadFolders(cfg.default_space_id); loadFolderlessLists(cfg.default_space_id); }}
                      className={`${btn} bg-muted hover:bg-muted text-foreground`}
                      data-testid="clickup-reload-lists">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </label>
        )}

        {/* Enable toggle */}
        <label className="flex items-center gap-2 pt-3 text-sm">
          <input type="checkbox" checked={!!cfg.enabled}
                 onChange={e => savePartial({ enabled: e.target.checked })}
                 data-testid="clickup-enabled" />
          Ativar integração ClickUp (necessário para os syncs futuros)
        </label>

        {cfg.enabled && !cfg.default_list_id && (
          <div className="rounded-md border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning-bg))] p-3 text-[13px] text-[hsl(var(--warning))] flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5" />
            <div>Selecione uma <b>List predefinida</b> antes de activar o sync. Sem List, o backend não sabe onde criar as tarefas.</div>
          </div>
        )}
      </div>

      {/* ---------------- STEP 3 · Canal de celebrações no ClickUp Chat ---------------- */}
      <div className={`bg-card border border-border rounded-2xl p-8 space-y-5 ${(!cfg.token_set || !cfg.workspace_id) ? "opacity-60 pointer-events-none" : ""}`}
           data-testid="clickup-celebration-block">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-full bg-muted border border-border flex items-center justify-center text-[13px] font-semibold text-muted-foreground">3</div>
          <h2 className="text-lg font-heading font-semibold">🎉 Canal de celebrações · adjudicações</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          Sempre que um cliente <b>aceita a proposta</b>, o Caring publica automaticamente uma mensagem no canal
          escolhido do <b>ClickUp Chat</b> com o resumo do serviço, escritório, arranque previsto, duração e
          parabéns ao <i>atendimento</i> + <i>gestor(a)</i>.
        </p>

        <label className="block">
          <span className="block text-[11px] uppercase tracking-widest text-muted-foreground mb-1">Canal (ClickUp Chat)</span>
          <div className="flex gap-2">
            <select value={cfg.celebration_channel_id || ""} className={inp}
                    disabled={loadingChat}
                    onChange={async (e) => {
                      const c = chatChannels.find(x => x.id === e.target.value);
                      await savePartial({
                        celebration_channel_id: c?.id || "",
                        celebration_channel_name: c?.name || "",
                      });
                    }}
                    data-testid="clickup-celebration-channel">
              <option value="">{loadingChat ? "A carregar canais…" : "— seleccionar canal —"}</option>
              {chatChannels.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <button onClick={loadChatChannels}
                    className={`${btn} bg-muted hover:bg-muted text-foreground`}
                    data-testid="clickup-reload-chat">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </label>

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button onClick={sendChatTest}
                  disabled={testingChat || !cfg.celebration_channel_id}
                  className={`${btn} bg-emerald-600 hover:bg-emerald-700 text-primary-foreground`}
                  data-testid="clickup-chat-test-btn">
            {testingChat ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Enviar teste no canal
          </button>
          <label className="flex items-center gap-2 text-sm ml-2">
            <input type="checkbox" checked={!!cfg.celebration_enabled}
                   onChange={e => savePartial({ celebration_enabled: e.target.checked })}
                   data-testid="clickup-celebration-enabled" />
            Ativar mensagem automática nas adjudicações
          </label>
        </div>

        {cfg.celebration_enabled && !cfg.celebration_channel_id && (
          <div className="rounded-md border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning-bg))] p-3 text-[13px] text-[hsl(var(--warning))] flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5" />
            <div>Escolha um canal antes de ativar. Sem canal, o Caring não pode publicar.</div>
          </div>
        )}
        {cfg.celebration_enabled && cfg.celebration_channel_id && (
          <div className="rounded-md border border-[hsl(var(--success))]/40 bg-[hsl(var(--success-bg))] p-3 text-[13px] text-[hsl(var(--success))] flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 mt-0.5" />
            <div>
              As celebrações vão para <b>{cfg.celebration_channel_name || "canal escolhido"}</b> assim
              que a próxima proposta for aceite. Podes desativar aqui a qualquer momento.
            </div>
          </div>
        )}
      </div>

      {/* ------- CHAT · TSC / Reuniões Técnicas — REMOVIDO (28/02/2026) -------
          O fluxo de submissão à TSC foi descontinuado. A Direção Técnica
          passou a receber notificação diretamente por email + link público
          de aprovação (portal DT). Se precisares de reactivar notificações
          no ClickUp para a DT, cria uma nova secção específica DT.  */}

      {/* ------- RT · Consultas Técnicas — Lista de tasks ------- */}
      <div className={`rounded-xl border border-border bg-card p-6 space-y-4 ${(!cfg.token_set || !cfg.workspace_id) ? "opacity-60 pointer-events-none" : ""}`}
           data-testid="clickup-rt-card">
        <h3 className="font-display text-2xl">RT · Consultas Técnicas (board de tasks)</h3>
        <p className="text-sm text-muted-foreground">
          Ao terminar uma RT, o consultor pode publicar o resumo directamente no board <b>Leads · Consultas Técnicas</b> do ClickUp.
          Cada resumo cria um <b>task</b> com status inicial <b>PENDENTE</b>. Se depois for reeditado, o mesmo task é <b>actualizado</b> (não cria duplicados).
        </p>
        <label className="block">
          <span className="block text-[11px] uppercase tracking-widest text-muted-foreground mb-1">
            Lista ClickUp — Leads · Consultas Técnicas
          </span>
          <div className="flex gap-2">
            <select value={cfg.rt_summary_list_id || ""} className={inp}
                    onChange={async (e) => {
                      const l = allListChoices.find(x => x.id === e.target.value);
                      await savePartial({
                        rt_summary_list_id: l?.id || "",
                        rt_summary_list_name: l?.name || "",
                      });
                    }}
                    data-testid="clickup-rt-list-select">
              <option value="">— seleccionar lista —</option>
              {allListChoices.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
            <button onClick={() => { loadFolders(cfg.default_space_id); loadFolderlessLists(cfg.default_space_id); }}
                    className={`${btn} bg-muted hover:bg-muted text-foreground`}
                    data-testid="clickup-rt-reload-lists"
                    title="Recarregar listas do space seleccionado">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">
            {cfg.rt_summary_list_id
              ? <>Lista atual: <b>{cfg.rt_summary_list_name || "—"}</b>. Cada RT concluída cria/actualiza um task nesta lista.</>
              : <>Sem lista configurada. Enquanto ficar assim, os resumos publicados caem no <b>canal SAC</b> como mensagem de chat (retro-compat).</>}
          </div>
        </label>
        <label className="block">
          <span className="block text-[11px] uppercase tracking-widest text-muted-foreground mb-1">
            Status inicial dos tasks (opcional)
          </span>
          <input type="text" className={inp}
                 value={cfg.rt_summary_initial_status || ""}
                 onChange={e => setCfg(c => ({ ...c, rt_summary_initial_status: e.target.value }))}
                 onBlur={e => savePartial({ rt_summary_initial_status: (e.target.value || "").trim() })}
                 placeholder="PENDENTE"
                 data-testid="clickup-rt-initial-status" />
          <div className="text-[11px] text-muted-foreground mt-1">
            Deve corresponder <b>exactamente</b> a um dos statuses definidos no board no ClickUp. Por defeito, <b>PENDENTE</b>.
          </div>
        </label>
        {cfg.rt_summary_list_id && (
          <div className="rounded-md border border-[hsl(var(--success))]/40 bg-[hsl(var(--success-bg))] p-3 text-[13px] text-[hsl(var(--success))] flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 mt-0.5" />
            <div>Resumos RT vão criar tasks em <b>{cfg.rt_summary_list_name || "lista"}</b> · status inicial <b>{cfg.rt_summary_initial_status || "PENDENTE"}</b>.</div>
          </div>
        )}
      </div>

      {/* ------- CHAT · SAC / Atendimento — Report Performance ------- */}
      <div className="rounded-xl border border-border bg-card p-6 space-y-4" data-testid="clickup-sac-card">
        <h3 className="font-display text-2xl">SAC · Report Performance & Arranque</h3>
        <p className="text-sm text-muted-foreground">
          O Caring pode publicar automaticamente <b>3 tipos</b> de mensagem no canal do <b>SAC — Gestão de Leads</b>:
        </p>
        <ul className="text-sm text-muted-foreground list-disc ml-5 space-y-1">
          <li><b>Arranque do dia</b> (09:00) — resumo dos leads pendentes e prioridades para atendimento começar já a mexer.</li>
          <li><b>Relatório diário</b> (20:00) — performance do dia (leads recebidos, tratados, tempo médio de resposta, breakdown por operador).</li>
          <li><b>Watcher de leads não tratados</b> — pinga uma pessoa específica quando um lead recebido em horário fica &gt; X min sem ser tratado.</li>
        </ul>

        <label className="block">
          <span className="block text-[11px] uppercase tracking-widest text-muted-foreground mb-1">Canal (ClickUp Chat) — SAC · Gestão de Leads</span>
          <div className="flex gap-2">
            <select value={cfg.sac_channel_id || ""} className={inp}
                    disabled={loadingChat}
                    onChange={async (e) => {
                      const c = chatChannels.find(x => x.id === e.target.value);
                      await savePartial({
                        sac_channel_id: c?.id || "",
                        sac_channel_name: c?.name || "",
                      });
                    }}
                    data-testid="clickup-sac-channel">
              <option value="">{loadingChat ? "A carregar canais…" : "— seleccionar canal —"}</option>
              {chatChannels.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <button onClick={loadChatChannels}
                    className={`${btn} bg-muted hover:bg-muted text-foreground`}
                    data-testid="clickup-sac-reload-chat">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">
            Escolha o canal <b>"SAC - Gestão de Leads"</b> na lista. Se não aparecer, clique no botão de refresh.
            Canal atual: <b>{cfg.sac_channel_name || "— nenhum —"}</b>.
          </div>
        </label>

        {/* Toggles: daily + morning */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="rounded-lg border border-border p-3 space-y-2 bg-muted/40">
            <label className="flex items-center gap-2 text-sm font-semibold">
              <input type="checkbox" checked={!!cfg.sac_morning_enabled}
                     onChange={e => savePartial({ sac_morning_enabled: e.target.checked })}
                     data-testid="clickup-sac-morning-enabled" />
              Arranque do dia (09:00)
            </label>
            <div className="text-[11px] text-muted-foreground">
              Publicado às 09:00 de cada dia útil. Última execução: <b>{cfg.sac_last_morning_at ? new Date(cfg.sac_last_morning_at).toLocaleString("pt-PT") : "— nunca —"}</b>.
            </div>
            <div className="flex gap-2">
              <button
                data-testid="clickup-sac-morning-preview"
                onClick={async () => {
                  setSacBusy("morning");
                  try {
                    const { data } = await api.get("/sac-reports/preview-morning");
                    setSacPreview(data?.content || "(vazio)");
                  } catch (e) {
                    toast.error(formatApiError(e, "Erro no preview"));
                  } finally { setSacBusy(""); }
                }}
                className={`${btn} bg-card border border-border hover:bg-muted text-foreground text-xs px-3 py-1`}>
                <Eye className="w-3 h-3" /> Preview
              </button>
              <button
                data-testid="clickup-sac-morning-run"
                disabled={sacBusy === "morning" || !cfg.sac_channel_id}
                onClick={async () => {
                  if (!cfg.sac_channel_id) { toast.error("Configure o canal SAC primeiro"); return; }
                  setSacBusy("morning");
                  try {
                    await api.post("/sac-reports/run-morning-now");
                    toast.success("Arranque publicado no ClickUp");
                    await load();
                  } catch (e) {
                    toast.error(formatApiError(e, "Erro ao enviar"));
                  } finally { setSacBusy(""); }
                }}
                className={`${btn} bg-foreground hover:bg-foreground/90 text-background text-xs px-3 py-1 disabled:opacity-50`}>
                {sacBusy === "morning" ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlayCircle className="w-3 h-3" />} Enviar agora
              </button>
            </div>
          </div>

          <div className="rounded-lg border border-border p-3 space-y-2 bg-muted/40">
            <label className="flex items-center gap-2 text-sm font-semibold">
              <input type="checkbox" checked={!!cfg.sac_daily_enabled}
                     onChange={e => savePartial({ sac_daily_enabled: e.target.checked })}
                     data-testid="clickup-sac-daily-enabled" />
              Relatório diário (20:00)
            </label>
            <div className="text-[11px] text-muted-foreground">
              Publicado às 20:00 de cada dia útil. Última execução: <b>{cfg.sac_last_daily_at ? new Date(cfg.sac_last_daily_at).toLocaleString("pt-PT") : "— nunca —"}</b>.
            </div>
            <div className="flex gap-2">
              <button
                data-testid="clickup-sac-daily-preview"
                onClick={async () => {
                  setSacBusy("daily");
                  try {
                    const { data } = await api.get("/sac-reports/preview-daily");
                    setSacPreview(data?.content || "(vazio)");
                  } catch (e) {
                    toast.error(formatApiError(e, "Erro no preview"));
                  } finally { setSacBusy(""); }
                }}
                className={`${btn} bg-card border border-border hover:bg-muted text-foreground text-xs px-3 py-1`}>
                <Eye className="w-3 h-3" /> Preview
              </button>
              <button
                data-testid="clickup-sac-daily-run"
                disabled={sacBusy === "daily" || !cfg.sac_channel_id}
                onClick={async () => {
                  if (!cfg.sac_channel_id) { toast.error("Configure o canal SAC primeiro"); return; }
                  setSacBusy("daily");
                  try {
                    await api.post("/sac-reports/run-daily-now");
                    toast.success("Relatório publicado no ClickUp");
                    await load();
                  } catch (e) {
                    toast.error(formatApiError(e, "Erro ao enviar"));
                  } finally { setSacBusy(""); }
                }}
                className={`${btn} bg-foreground hover:bg-foreground/90 text-background text-xs px-3 py-1 disabled:opacity-50`}>
                {sacBusy === "daily" ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlayCircle className="w-3 h-3" />} Enviar agora
              </button>
            </div>
          </div>
        </div>

        {/* Preview panel */}
        {sacPreview && (
          <div className="rounded-lg border border-border bg-card p-3" data-testid="clickup-sac-preview">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Preview (não enviado)</span>
              <button onClick={() => setSacPreview("")}
                      className="text-[11px] text-muted-foreground hover:text-foreground">fechar</button>
            </div>
            <pre className="text-[11px] whitespace-pre-wrap break-words text-foreground font-mono max-h-64 overflow-y-auto">{sacPreview}</pre>
          </div>
        )}

        {/* Watcher: leads não tratados */}
        <div className="rounded-lg border border-border bg-muted/40 p-3 space-y-3">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-foreground" />
            <span className="text-sm font-semibold">Watcher — leads não tratados</span>
          </div>
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            Dispara mensagem privada no ClickUp (à responsável do SAC) quando um lead recebido em horário
            fica &gt; X minutos sem ser tratado por nenhum operador.
          </p>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!cfg.untreated_alert_enabled}
                   onChange={e => savePartial({ untreated_alert_enabled: e.target.checked })}
                   data-testid="clickup-untreated-enabled" />
            Ativar watcher
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <label className="block md:col-span-2">
              <span className="block text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Responsável (ClickUp user)</span>
              {clickupUsers.length > 0 ? (
                <select className={inp}
                        value={cfg.untreated_alert_watcher_id || ""}
                        onChange={async (e) => {
                          const u = clickupUsers.find(x => x.id === e.target.value);
                          await savePartial({
                            untreated_alert_watcher_id: u?.id || "",
                            untreated_alert_watcher_name: u?.username || "",
                          });
                        }}
                        data-testid="clickup-untreated-watcher-picker">
                  <option value="">— selecionar pessoa —</option>
                  {clickupUsers.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.username}{u.email ? ` · ${u.email}` : ""} (id {u.id})
                    </option>
                  ))}
                </select>
              ) : (
                <div className="flex gap-2">
                  <input className={inp}
                         value={cfg.untreated_alert_watcher_id || ""}
                         onChange={e => setCfg(c => ({ ...c, untreated_alert_watcher_id: e.target.value }))}
                         onBlur={e => savePartial({ untreated_alert_watcher_id: e.target.value })}
                         placeholder="ClickUp user_id (ex: 106673245)"
                         data-testid="clickup-untreated-watcher-id" />
                  <button onClick={loadClickupUsers}
                          className={`${btn} bg-muted hover:bg-muted text-foreground`}
                          title="Carregar lista de pessoas do ClickUp"
                          data-testid="clickup-untreated-watcher-reload">
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>
              )}
              <div className="text-[10px] text-muted-foreground mt-1">
                {clickupUsers.length > 0
                  ? `${clickupUsers.length} pessoa(s) carregada(s) do workspace. Actual: ${cfg.untreated_alert_watcher_name || "—"}.`
                  : "Não conseguiu carregar a lista — introduza o user_id manualmente ou clique no ícone ↻."}
              </div>
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Threshold (minutos)</span>
              <input type="number" min="5" max="120" step="5" className={inp}
                     value={cfg.untreated_alert_minutes || 15}
                     onChange={e => setCfg(c => ({ ...c, untreated_alert_minutes: parseInt(e.target.value || "15", 10) }))}
                     onBlur={e => savePartial({ untreated_alert_minutes: parseInt(e.target.value || "15", 10) })}
                     data-testid="clickup-untreated-minutes" />
            </label>
          </div>
          <div className="flex justify-end">
            <button
              data-testid="clickup-untreated-sweep-run"
              disabled={sacBusy === "watcher" || !cfg.untreated_alert_enabled}
              onClick={async () => {
                setSacBusy("watcher");
                try {
                  const { data } = await api.post("/sac-reports/run-untreated-sweep");
                  toast.success(`Sweep executado — ${data?.notified || 0} pinged`);
                } catch (e) {
                  toast.error(formatApiError(e, "Erro no sweep"));
                } finally { setSacBusy(""); }
              }}
              className={`${btn} bg-foreground hover:bg-foreground/90 text-background text-xs px-3 py-1 disabled:opacity-50`}>
              {sacBusy === "watcher" ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlayCircle className="w-3 h-3" />} Correr sweep agora
            </button>
          </div>
        </div>

        {/* Watcher: callback sem resposta */}
        <div className="rounded-lg border border-border bg-muted/40 p-3 space-y-3">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-[hsl(var(--warning))]" />
            <span className="text-sm font-semibold">Watcher — callback sem resposta do cliente</span>
          </div>
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            Depois de um lead ficar no estado "Tentativa de contacto" enviamos automaticamente um email
            ao cliente (a partir de sac.qi@caring.pt) com opções de reagendar ou desistir. Se não houver
            resposta em X horas, mandamos um alerta no canal SAC do ClickUp para a mesma pessoa configurada
            acima (o watcher SAC).
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <label className="block md:col-span-2">
              <span className="block text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Depende do watcher SAC acima</span>
              <div className="text-xs text-foreground py-2 px-3 border border-border rounded bg-card">
                {cfg.untreated_alert_enabled
                  ? <><CheckCircle2 className="w-3 h-3 text-[hsl(var(--success))] inline mr-1" /> Ativo — usa <b>{cfg.untreated_alert_watcher_name || "—"}</b> e o canal SAC.</>
                  : <><AlertTriangle className="w-3 h-3 text-[hsl(var(--warning))] inline mr-1" /> Watcher SAC desativado — ative o watcher acima para este alerta também correr.</>}
              </div>
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Threshold (horas)</span>
              <input type="number" min="6" max="240" step="6" className={inp}
                     value={cfg.callback_alert_hours || 48}
                     onChange={e => setCfg(c => ({ ...c, callback_alert_hours: parseInt(e.target.value || "48", 10) }))}
                     onBlur={e => savePartial({ callback_alert_hours: parseInt(e.target.value || "48", 10) })}
                     data-testid="clickup-callback-hours" />
            </label>
          </div>
          <div className="flex justify-end">
            <button
              data-testid="clickup-callback-sweep-run"
              disabled={sacBusy === "callback" || !cfg.untreated_alert_enabled}
              onClick={async () => {
                setSacBusy("callback");
                try {
                  const { data } = await api.post("/sac-reports/run-callback-sweep");
                  const scanned = data?.scanned || 0;
                  const alerted = data?.alerted || 0;
                  toast.success(`Sweep executado — ${alerted} alerta(s) enviado(s) de ${scanned} verificado(s)`);
                } catch (e) {
                  toast.error(formatApiError(e, "Erro no sweep"));
                } finally { setSacBusy(""); }
              }}
              className={`${btn} bg-orange-600 hover:bg-orange-700 text-primary-foreground text-xs px-3 py-1 disabled:opacity-50`}>
              {sacBusy === "callback" ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlayCircle className="w-3 h-3" />} Correr sweep de callbacks agora
            </button>
          </div>
        </div>

        {(cfg.sac_daily_enabled || cfg.sac_morning_enabled) && !cfg.sac_channel_id && (
          <div className="rounded-md border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning-bg))] p-3 text-[13px] text-[hsl(var(--warning))] flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5" />
            <div>Escolha o canal SAC — sem canal, as mensagens automáticas não são publicadas.</div>
          </div>
        )}
        {cfg.sac_channel_id && (cfg.sac_daily_enabled || cfg.sac_morning_enabled) && (
          <div className="rounded-md border border-[hsl(var(--success))]/40 bg-[hsl(var(--success-bg))] p-3 text-[13px] text-[hsl(var(--success))] flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 mt-0.5" />
            <div>
              As mensagens SAC vão para <b>{cfg.sac_channel_name || "canal escolhido"}</b>.
              {cfg.sac_morning_enabled && " Arranque 09:00 ativo."}
              {cfg.sac_daily_enabled && " Relatório 20:00 ativo."}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
